import React, { useMemo } from "react";
import { Box, Card, CardMedia, Stack, Typography, Avatar } from "@mui/material";
import { Check as CheckIcon, Done as DoneIcon } from "@mui/icons-material";
import { ChatMessage } from "./chatTypes";

interface MessageBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
  showAvatar?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  showAvatar = true,
}) => {
  const formattedTime = useMemo(() => {
    const date = new Date(message.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
    }
  }, [message.timestamp]);

  const hasReadReceipts = message.read_by && message.read_by.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOwn ? "flex-end" : "flex-start",
        alignItems: "flex-end",
        gap: 1,
        mb: 1.5,
        px: 2,
      }}
    >
      {!isOwn && showAvatar && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "primary.main",
            fontSize: "0.875rem",
          }}
        >
          {message.sender_role.charAt(0).toUpperCase()}
        </Avatar>
      )}

      <Stack
        spacing={0.5}
        sx={{ maxWidth: "70%", alignItems: isOwn ? "flex-end" : "flex-start" }}
      >
        <Card
          sx={{
            bgcolor: isOwn ? "primary.main" : "action.hover",
            color: isOwn ? "primary.contrastText" : "text.primary",
            borderRadius: 2,
            p: 1.5,
            wordBreak: "break-word",
          }}
        >
          {message.attachment_url && message.signed_url ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <CardMedia
                component="img"
                image={message.signed_url}
                alt="Message attachment"
                sx={{
                  borderRadius: 1,
                  maxHeight: "300px",
                  maxWidth: "300px",
                  objectFit: "cover",
                }}
              />
              {message.text && (
                <Typography variant="body2">{message.text}</Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2">{message.text}</Typography>
          )}
        </Card>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            alignItems: "center",
            justifyContent: isOwn ? "flex-end" : "flex-start",
            px: 1,
            mt: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formattedTime}
          </Typography>
          {isOwn && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {hasReadReceipts ? (
                <DoneIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
              ) : (
                <CheckIcon sx={{ fontSize: "1rem" }} />
              )}
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default MessageBubble;
